import React, { useMemo, useState } from 'react';
import { SegmentedControl } from '@mantine/core';
import clsx from 'clsx';
import { MdDownload, MdOutlineFileOpen, MdOutlineFindInPage } from 'react-icons/md';
import Button from '@srm/ui/button';
import FormGroup from '@srm/ui/form-group';
import Hint from '@srm/ui/hint';
import Modal from '@srm/ui/modal';
import { IOfferAction } from '@models/winnerSelection/ITableDemand';
import {
    IMatrixCell,
    IMatrixCellCommand,
    IMatrixParticipant,
    IMatrixPosition,
    IWinnerMatrix,
    MatrixResult,
} from '../WinnerSelection2.types';
import {
    cellAmount,
    formatMatrixMessage,
    complianceAllowsSelection,
    formatFieldValue,
    formatMoney,
    formatQuantity,
    exceedsRequired,
    priceModeShort,
    resultLabel,
} from '../utils/matrixView';
import { commentRequiredFor, complianceOf, parseQuantityInput, quantityErrorOf } from '../utils/dialogForm';
import { DialogContext } from './DialogContext';
import { ApplyToSupplierOffers, ComplianceFields, QuantityField } from './DialogFields';
import MatrixWarning from './MatrixWarning';

export type OfferActionIntent = 'open' | 'download' | 'customForm';

interface IProps {
    matrix: IWinnerMatrix;
    cell: IMatrixCell;
    position: IMatrixPosition;
    participant: IMatrixParticipant;
    busy: boolean;
    blocked?: boolean;
    error: string | undefined;
    /** Остальные предложения поставщика с правом ответа по ТЗ: ключ → ревизия. */
    otherOffers: Record<string, string>;
    onSave: (command: Omit<IMatrixCellCommand, 'offerKey' | 'positionKey' | 'expectedRevision'>) => void;
    onAction: (intent: OfferActionIntent, action: IOfferAction) => void;
    onClose: () => void;
}

const ACTION_INTENT: Partial<Record<IOfferAction['type'], OfferActionIntent>> = {
    OpenOffer: 'open',
    DownloadAttachments: 'download',
    CustomForm: 'customForm',
};

const ACTION_ICON: Record<OfferActionIntent, React.ReactNode> = {
    open: <MdOutlineFileOpen size="18px" aria-hidden />,
    download: <MdDownload size="18px" aria-hidden />,
    customForm: <MdOutlineFindInPage size="18px" aria-hidden />,
};

const lotHint = (lotOffer: boolean, fixed: boolean) => {
    if (!lotOffer) return 'В этом лоте выбирается количество, предложенное участником';
    return fixed
        ? 'В этом лоте результат выбирается для всего лота, количество — предложенное участником'
        : 'В этом лоте результат выбирается для всего лота. Количество позиции можно изменить';
};

/**
 * Детали предложения: цена в режиме НДС закупки, предложенное и выбранное
 * количество, результат и ТЗ сохраняются одной командой. В режиме просмотра
 * поля неизменяемы и кнопки «Сохранить» нет.
 */
const OfferDetailsDialog: React.FC<IProps> = ({
    matrix,
    cell,
    position,
    participant,
    busy,
    blocked,
    error,
    otherOffers,
    onSave,
    onAction,
    onClose,
}) => {
    const answers = matrix.compliance.answers;
    const initialQuantity = cell.effectiveQuantity ?? cell.selectedQuantity;
    const [result, setResult] = useState<MatrixResult>(cell.result);
    const [quantity, setQuantity] = useState(String(initialQuantity ?? 0));
    const [answer, setAnswer] = useState(cell.complianceAnswer ?? '');
    const [comment, setComment] = useState(cell.complianceComment ?? '');
    const [submitted, setSubmitted] = useState(false);
    const [applyToAll, setApplyToAll] = useState(false);

    const canDecision = cell.canEditQuantity;
    const canCompliance = cell.canEditCompliance;
    const fixed = cell.quantityFixed;
    const editable = canDecision || canCompliance;
    const compliance = complianceOf(answers, answer || null);
    const selectionAllowed = complianceAllowsSelection(compliance);
    const parsedQuantity = fixed ? cell.offeredQuantity : parseQuantityInput(quantity);
    const assigned = result === 'main' || result === 'reserve';
    const unit = cell.unit ?? position.unit;

    const overRequired = exceedsRequired(matrix, cell, position, result, parsedQuantity);
    const quantityError = assigned ? quantityErrorOf(parsedQuantity, cell.offeredQuantity, submitted) : undefined;
    const commentError =
        submitted && commentRequiredFor(answers, answer) && !comment.trim()
            ? 'Для частичного или отрицательного ответа нужен комментарий'
            : undefined;

    const resultOptions = useMemo(() => {
        const options: { value: string; label: string; disabled?: boolean }[] = [
            { value: 'none', label: 'Не выбран' },
            { value: 'main', label: 'Основной', disabled: !selectionAllowed },
            { value: 'reserve', label: 'Резервный', disabled: !selectionAllowed },
        ];
        if (cell.result === 'other')
            options.push({ value: 'other', label: resultLabel('other', cell.resultText), disabled: true });
        return options;
    }, [selectionAllowed, cell.result, cell.resultText]);

    const actions = cell.actions
        .map((action) => ({ action, intent: ACTION_INTENT[action.type] }))
        .filter(
            (item): item is { action: IOfferAction; intent: OfferActionIntent } => !!item.intent && item.action.visible,
        );

    const save = () => {
        setSubmitted(true);
        const complianceChanged =
            canCompliance &&
            (applyToAll || answer !== (cell.complianceAnswer ?? '') || comment !== (cell.complianceComment ?? ''));
        if (complianceChanged && commentRequiredFor(answers, answer) && !comment.trim()) return;

        // Ответ ТЗ, не допускающий выбор, снимает назначение на сервере с сохранением
        // количества — решение в этом случае не отправляем.
        const withdrawn = assigned && !selectionAllowed;
        const quantityChanged = parsedQuantity !== initialQuantity;
        const decisionChanged =
            canDecision &&
            !withdrawn &&
            result !== 'other' &&
            (result !== cell.result || (assigned && quantityChanged));
        if (decisionChanged && assigned && quantityErrorOf(parsedQuantity, cell.offeredQuantity, true)) return;
        if (!complianceChanged && !decisionChanged) {
            onClose();
            return;
        }

        onSave({
            decision: decisionChanged
                ? { result, selectedQuantity: result === 'none' ? null : parsedQuantity }
                : undefined,
            compliance: complianceChanged
                ? {
                      answer: answer || null,
                      comment: comment.trim() || null,
                      otherOfferRevisions: applyToAll ? otherOffers : undefined,
                  }
                : undefined,
        });
    };

    const footer = (
        <div className="ws2-dialog__footer">
            <Button variant="outline-neutral" disabled={busy} onClick={onClose}>
                {editable ? 'Отмена' : 'Закрыть'}
            </Button>
            {editable && (
                <Button variant="primary" onClick={save} loading={busy} disabled={busy || blocked}>
                    Сохранить
                </Button>
            )}
        </div>
    );

    return (
        <Modal
            className="ws2-modal"
            width={820}
            size="l"
            header="Детали предложения"
            onClose={onClose}
            hasUnsavedInput={() =>
                editable &&
                (applyToAll ||
                    result !== cell.result ||
                    quantity !== String(initialQuantity ?? 0) ||
                    answer !== (cell.complianceAnswer ?? '') ||
                    comment !== (cell.complianceComment ?? ''))
            }
            closeOnEscape={!busy}
            closeDisabled={busy}
            footer={footer}
        >
            <div className="ws2-dialog">
                <DialogContext supplier={participant.name} subject={position.name} />
                {error && <Hint role="alert" title={error} tone="danger" maxWidth="100%" />}

                <div className="ws2-dialog__detail-layout">
                    <div className="ws2-dialog__numbers">
                        <div>
                            <span>Цена за единицу, {priceModeShort(matrix.priceMode)}</span>
                            <strong>{matrix.priceVisible ? formatMoney(cell.price, cell.currency) : 'Скрыта'}</strong>
                        </div>
                        <div>
                            <span>Стоимость выбранного объёма</span>
                            <strong>
                                {matrix.priceVisible && assigned
                                    ? formatMoney(cellAmount(cell, parsedQuantity), cell.currency)
                                    : '—'}
                            </strong>
                        </div>
                        <div>
                            <span>Предложено участником</span>
                            <strong>
                                {formatQuantity(cell.offeredQuantity)} {unit}
                            </strong>
                        </div>
                        <div>
                            <span>Потребность позиции</span>
                            <strong>
                                {formatQuantity(position.requiredQuantity)} {position.unit}
                            </strong>
                        </div>
                        {cell.rank != null && (
                            <div>
                                <span>Ранг (информационно)</span>
                                <strong>{cell.rank}</strong>
                            </div>
                        )}
                        {matrix.offerDetailsFields?.map((field) => (
                            <div
                                key={field.key}
                                className={clsx(field.format === 'text' && 'ws2-dialog__text-value')}
                                data-testid="ws2-offer-field"
                            >
                                <span>{field.title}</span>
                                <strong>{formatFieldValue(field, cell.fields?.[field.key])}</strong>
                            </div>
                        ))}
                    </div>

                    <div className="ws2-dialog__form">
                        {cell.priceIsRubFallback && (
                            <Hint title="Цены в валюте нет, показана цена в рублях" tone="warning" maxWidth="100%" />
                        )}
                        {cell.selectedQuantityInvalid && (
                            <Hint
                                title={`Сохранённое количество «${cell.selectedQuantityInvalid}» не распознано. Укажите число`}
                                tone="warning"
                                maxWidth="100%"
                            />
                        )}

                        <QuantityField
                            value={fixed ? String(cell.offeredQuantity ?? '') : quantity}
                            unit={unit}
                            // Без назначения количество не сохраняется: сброс пишет 0.
                            disabled={!canDecision || !assigned || fixed || busy || blocked}
                            invalid={!!quantityError}
                            errorMessage={quantityError}
                            onChange={setQuantity}
                        />

                        {overRequired && (
                            <div className="ws2-dialog__warnings">
                                <MatrixWarning>С учётом других назначений объём превышает потребность</MatrixWarning>
                            </div>
                        )}

                        <ComplianceFields
                            answers={answers}
                            answer={answer}
                            comment={comment}
                            disabled={!canCompliance || busy || blocked}
                            commentError={commentError}
                            onAnswer={setAnswer}
                            onComment={setComment}
                        />
                        {canCompliance && (
                            <ApplyToSupplierOffers
                                count={Object.keys(otherOffers).length}
                                checked={applyToAll}
                                disabled={busy || blocked}
                                onChange={setApplyToAll}
                            />
                        )}
                        <FormGroup label="Результат выбора">
                            <SegmentedControl
                                className="ws2-dialog__result-segments"
                                aria-label="Результат выбора"
                                fullWidth
                                radius="sm"
                                data={resultOptions}
                                value={result}
                                disabled={!canDecision || cell.lotOffer || busy || blocked}
                                onChange={(value) => setResult(value as MatrixResult)}
                            />
                        </FormGroup>
                        {(cell.lotOffer || fixed) && (
                            <Hint title={lotHint(cell.lotOffer, fixed)} tone="info" maxWidth="100%" />
                        )}
                        {result === 'none' && cell.result !== 'none' && canDecision && (
                            <Hint
                                role="alert"
                                title="Сброс очистит результат и выбранное количество. Ответ ТЗ и комментарий сохранятся"
                                tone="warning"
                                maxWidth="100%"
                            />
                        )}

                        {!selectionAllowed && (cell.result === 'main' || cell.result === 'reserve') && (
                            <Hint
                                role="alert"
                                title="Этот ответ по ТЗ не допускает выбор: назначение будет снято, выбранное количество сохранится"
                                tone="warning"
                                maxWidth="100%"
                            />
                        )}

                        {!editable && (
                            <Hint
                                title={formatMatrixMessage(
                                    cell.decisionReason ?? 'Изменять это предложение вам сейчас нельзя',
                                )}
                                tone="info"
                                maxWidth="100%"
                            />
                        )}
                    </div>
                </div>
                {actions.length > 0 && (
                    <div className="ws2-dialog__documents" role="group" aria-label="Действия с предложением">
                        {actions.map(({ action, intent }) => (
                            <button
                                type="button"
                                className="ws2-dialog__document-action"
                                key={action.key}
                                disabled={busy}
                                onClick={() => onAction(intent, action)}
                            >
                                {ACTION_ICON[intent]}
                                <span>{action.displayName}</span>
                            </button>
                        ))}
                    </div>
                )}
            </div>
        </Modal>
    );
};

export default OfferDetailsDialog;
